import angular = require("angular");

export interface ExportColumn {
  key: string;
  label: string;
  category: string;
  clientType: 'All' | 'Individual' | 'Institution';
  selected: boolean;
  isMainColumn?: boolean;
}

export interface ClientExportColumnsModalData {
  availableColumns: ExportColumn[];
  onExport: (params: { exportParams: { selectedColumns: string[]; exportAllClients: boolean } }) => void;
  onCancel: () => void;
}

class ClientExportColumnsModalController implements angular.IComponentController {
  static $inject = ['clientRepo'];

  public onExport: (params: { exportParams: { selectedColumns: string[]; exportAllClients: boolean } }) => void;
  public onCancel: () => void;

  public availableColumns: ExportColumn[] = [];
  public filteredColumns: ExportColumn[] = [];
  public filterCategory: string = 'All';
  public searchTerm: string = '';
  public selectAllChecked: boolean = false;
  public isLoading: boolean = true;
  public exportAllClients: boolean = false; // Default to false (use filtered results)

  public categories: string[] = [];

  // Main columns from the clients table - these should appear first and be auto-selected
  // Note: Keys match backend property names (PascalCase)
  private mainColumnKeys: string[] = [
    'ClientNumber',
    'Name',
    'Status',
    'InvestmentOfficer',
    'RelationshipManager',
    'AdministrativeOfficer'
  ];

  constructor(
    private clientRepo: any
  ) {}

  $onInit() {
    this.loadColumns();
  }

  private loadColumns() {
    this.isLoading = true;
    
    this.clientRepo.getAvailableExportColumns()
      .then((response: angular.IHttpPromiseCallbackArg<any>) => {
        const columns = response.data;
        
        // Mark main columns and sort them to appear first
        // Ensure Name and ClientNumber are always selected
        const requiredColumns = ['ClientNumber', 'Name'];
        const processedColumns = columns.map((col: any) => {
          const isMainColumn = this.mainColumnKeys.includes(col.key);
          const isRequired = requiredColumns.includes(col.key);
          return {
            ...col,
            selected: isMainColumn || isRequired, // Auto-select main columns and required columns
            isMainColumn: isMainColumn
          };
        });

        // Sort: main columns first, then others
        this.availableColumns = processedColumns.sort((a: ExportColumn, b: ExportColumn) => {
          if (a.isMainColumn && !b.isMainColumn) return -1;
          if (!a.isMainColumn && b.isMainColumn) return 1;
          return a.label.localeCompare(b.label);
        });
        
        this.loadCategories();
        this.filterColumns();
      })
      .catch((error) => {
        console.error('Error fetching available columns:', error);
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  private loadCategories() {
    const uniqueCategories = new Set<string>();
    this.availableColumns.forEach(col => uniqueCategories.add(col.category));
    this.categories = ['All', ...Array.from(uniqueCategories).sort()];
  }

  public filterColumns() {
    this.filteredColumns = this.availableColumns.filter(col => {
      const categoryMatch = this.filterCategory === 'All' || col.category === this.filterCategory;
      const searchMatch = !this.searchTerm || 
                         col.label.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
                         col.key.toLowerCase().includes(this.searchTerm.toLowerCase());
      return categoryMatch && searchMatch;
    });
    this.updateSelectAllState();
  }

  public toggleSelectAll() {
    const allVisibleSelected = this.filteredColumns.every(col => col.selected);
    this.filteredColumns.forEach(col => {
      col.selected = !allVisibleSelected;
    });
    this.selectAllChecked = !allVisibleSelected;
  }

  public updateSelectAllState() {
    this.selectAllChecked = this.filteredColumns.length > 0 && 
                           this.filteredColumns.every(col => col.selected);
  }

  public getSelectedCount(): number {
    return this.availableColumns.filter(col => col.selected).length;
  }

  public exportSelected() {
    const selectedKeys = this.availableColumns
      .filter(col => col.selected)
      .map(col => col.key);
    
    if (selectedKeys.length === 0) {
      alert('Please select at least one column to export.');
      return;
    }

    if (this.onExport) {
      this.onExport({ exportParams: { selectedColumns: selectedKeys, exportAllClients: this.exportAllClients } });
    }
  }

  public cancel() {
    if (this.onCancel) {
      this.onCancel();
    }
  }
}

export const clientExportColumnsModal: angular.IComponentOptions = {
  template: require("./client-export-columns-modal.component.html"),
  controller: ClientExportColumnsModalController,
  bindings: {
    onExport: "&",
    onCancel: "&"
  }
};




// WEBPACK FOOTER //
// ./src/app/clients/modals/client-export-columns-modal.component.ts